import React, { memo, useMemo, useState } from 'react';
import Avatar from './Avatar';
import { CoinIcon, FloatIcon, RubyIcon } from './CurrencyIcons';
import AlertModal from './AlertModal';
import { resolveDisplayName } from '../utils/telegramUser';
import { iconUrl } from '../utils/assets';
import { useLocale } from '../i18n/LocaleContext';
import { balanceWithPending } from '../utils/clickerMerge';

const fmt = (v, digits = 1) => {
  const n = parseFloat(v);
  if (!Number.isFinite(n)) return (0).toFixed(digits);
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(2)}M`;
  if (n >= 1000) return `${(n / 1000).toFixed(1)}k`;
  return n.toFixed(digits);
};

const fmtInt = (v) => {
  const n = parseInt(v, 10) || 0;
  return n.toLocaleString('ru-RU');
};

const formatJoined = (iso, lang) => {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    return d.toLocaleDateString(lang === 'en' ? 'en-GB' : 'ru-RU', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    });
  } catch {
    return '—';
  }
};

const AVATAR = 46;

const Header = memo(function Header({ userData, clickerData, onRubyHelp }) {
  const { t, lang, setLang } = useLocale();
  const [profileOpen, setProfileOpen] = useState(false);
  const prestige = clickerData?.prestige_level || 0;

  const rows = useMemo(() => {
    // Twelve formatted rows, two of them going through Intl — and none of it is
    // on screen until the player opens their profile. Building it on every
    // clickerData change meant paying for it on every energy tick.
    if (!profileOpen) return [];
    const stats = clickerData?.stats || {};
    const amberNow = clickerData?.amber ?? 0;
    const liveBal = balanceWithPending(clickerData);
    return [
      { label: t('statJoined'), value: formatJoined(stats.joined_at, lang) },
      { label: t('statTaps'), value: fmtInt(stats.total_taps ?? clickerData?.total_taps) },
      {
        label: t('statLifetimeCoins'),
        value: fmt(stats.lifetime_coins ?? clickerData?.lifetime_coins ?? liveBal, 2),
        icon: 'coin',
      },
      {
        label: t('statBalance'),
        value: fmt(liveBal, 2),
        icon: 'coin',
      },
      { label: t('statRubies'), value: String(stats.amber ?? amberNow ?? 0), icon: 'ruby' },
      {
        label: t('statRubiesLifetime'),
        value: String(stats.amber_lifetime ?? clickerData?.amber_lifetime ?? 0),
        icon: 'ruby',
      },
      {
        label: t('statPrestige'),
        value: `★${stats.prestige_level ?? prestige} · ×${stats.prestige_multiplier || clickerData?.prestige_multiplier || 1}`,
      },
      { label: t('statReferrals'), value: String(stats.invite_count ?? 0) },
      {
        label: t('statClan'),
        value: stats.clan_tag
          ? `[${stats.clan_tag}] ${stats.clan_name || ''}`.trim()
          : t('statNoClan'),
      },
      {
        label: t('statPerTap'),
        value: fmt(stats.coin_per_click ?? clickerData?.coin_per_click, 4),
        icon: 'coin',
      },
      {
        label: t('statPerHour'),
        value: fmt(stats.coin_per_hour ?? clickerData?.coin_per_hour, 2),
        icon: 'coin',
      },
      {
        label: t('statEnergyCap'),
        value: fmt(stats.max_energy ?? clickerData?.max_energy, 2),
      },
    ];
  }, [profileOpen, clickerData, prestige, lang, t]);

  const amber = clickerData?.amber ?? 0;
  const name = resolveDisplayName(userData);
  const titleRaw = clickerData?.equipped?.title_name || clickerData?.equipped?.title || '';
  const titleEn = clickerData?.equipped?.title_name_en || '';
  const title = lang === 'en' ? titleEn || titleRaw : titleRaw;
  const titleCode = clickerData?.equipped?.title || '';
  const frame = clickerData?.equipped?.frame || 'none';
  const coinPerHour = fmt(clickerData?.coin_per_hour);
  const coinPerClick = fmt(clickerData?.coin_per_click, 2);

  return (
    <>
      <header className="header-bar glass-panel" style={{ '--hdr-h': `${AVATAR}px` }}>
        <button
          type="button"
          className="header-avatar-btn"
          onClick={() => setProfileOpen(true)}
          aria-label={t('profileStats')}
          style={{ width: AVATAR, height: AVATAR, minWidth: AVATAR, minHeight: AVATAR }}
        >
          <span
            className={`profile-avatar-wrap frame-${frame}`}
            style={{
              '--av': `${AVATAR}px`,
              width: AVATAR,
              height: AVATAR,
              minWidth: AVATAR,
              minHeight: AVATAR,
            }}
          >
            <Avatar userData={userData} size={AVATAR} />
          </span>
        </button>

        <div className="header-mid">
          <div className="header-id">
            <span className="header-name">{name}</span>
            {prestige ? <span className="prestige-pill">★{prestige}</span> : null}
            {title ? <span className={`header-title title-${titleCode}`}>{title}</span> : null}
          </div>
          <div className="header-rates" aria-label="rates">
            <span className="hdr-chip" title={t('perHour')}>
              <CoinIcon size={11} />
              <b>{coinPerHour}</b>
              <i>{t('perHour')}</i>
            </span>
            <span className="hdr-chip" title={t('perTap')}>
              <CoinIcon size={11} />
              <b>{coinPerClick}</b>
              <i>{t('perTap')}</i>
            </span>
          </div>
        </div>

        <div className="header-right">
          <span className="hdr-chip hdr-ruby">
            <RubyIcon size={12} />
            <b className="amber-balance">{amber}</b>
            <button
              type="button"
              className="ruby-help-btn"
              aria-label={t('howEarnRubies')}
              onClick={onRubyHelp}
            >
              <FloatIcon src={iconUrl('question.png')} size={13} eager animated native />
            </button>
          </span>
          <button
            type="button"
            className="lang-toggle header-lang"
            aria-label={t('language')}
            onClick={() => setLang(lang === 'ru' ? 'en' : 'ru')}
          >
            {lang === 'ru' ? 'EN' : 'RU'}
          </button>
        </div>
      </header>

      {profileOpen ? (
        <AlertModal
          open
          icon="handshake.png"
          title={name}
          body={title || t('profileStats')}
          onClose={() => setProfileOpen(false)}
          confirmLabel={t('gotIt')}
        >
          <div className="profile-stats-grid">
            {rows.map((r) => (
              <div className="profile-stat-row" key={r.label}>
                <span className="profile-stat-label">{r.label}</span>
                <span className="profile-stat-value">
                  {r.icon === 'coin' ? <CoinIcon size={14} /> : null}
                  {r.icon === 'ruby' ? <RubyIcon size={14} /> : null}
                  {r.value}
                </span>
              </div>
            ))}
          </div>
        </AlertModal>
      ) : null}
    </>
  );
});

export default Header;
